// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/CrmPipeline.tsx
//  CRM — Pipeline / Funil de Vendas (Kanban com drag-and-drop nativo HTML5)
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import {
  Plus, Settings, Trophy, XCircle, GripVertical, DollarSign,
  Calendar, User, MoreVertical, Trash2, Pencil, TrendingUp,
} from 'lucide-react';
import {
  useEstagiosPipeline, useVisaoPipeline, usePipelineMutations,
} from '@/features/operacional/hooks/usePipeline';
import type {
  EstagioPipeline, OportunidadeResumo, CriarOportunidadeRequest,
  CriarEstagioRequest,
} from '@/features/operacional/types';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';

const fmt = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const CrmPipeline = () => {
  const { data: visao, isLoading } = useVisaoPipeline();
  const { data: estagios = [] } = useEstagiosPipeline();
  const m = usePipelineMutations();

  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dragOver, setDragOver]     = useState<number | null>(null);

  const [novaOpModal, setNovaOpModal] = useState<{ open: boolean; estagioId?: number }>({ open: false });
  const [novoEstagioModal, setNovoEstagioModal] = useState(false);
  const [editandoEstagio, setEditandoEstagio]   = useState<EstagioPipeline | null>(null);
  const [removeAlert, setRemoveAlert] = useState<{ tipo: 'estagio' | 'oportunidade'; id: number } | null>(null);
  const [fecharModal, setFecharModal] = useState<{ open: boolean; oportunidade?: OportunidadeResumo; ganho?: boolean }>({ open: false });

  const totaisGerais = useMemo(() => {
    if (!visao) return { total: 0, valor: 0 };
    return visao.colunas.reduce(
      (acc, c) => ({ total: acc.total + c.quantidadeOportunidades, valor: acc.valor + c.valorTotal }),
      { total: 0, valor: 0 },
    );
  }, [visao]);

  const handleDragStart = (id: number) => setDraggingId(id);
  const handleDragEnd   = () => { setDraggingId(null); setDragOver(null); };
  const handleDragOver  = (e: React.DragEvent, estagioId: number) => {
    e.preventDefault();
    setDragOver(estagioId);
  };
  const handleDrop = (estagioId: number) => {
    if (draggingId !== null) {
      m.moverOportunidade.mutate({ id: draggingId, data: { estagioNovoId: estagioId } });
    }
    handleDragEnd();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <TrendingUp className="h-7 w-7 text-primary" /> Pipeline de Vendas
          </h1>
          <p className="text-muted-foreground mt-1">
            Arraste os cartões entre estágios para mover as oportunidades
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setNovoEstagioModal(true)} className="gap-2">
            <Settings className="h-4 w-4" /> Novo Estágio
          </Button>
          <Button onClick={() => setNovaOpModal({ open: true })} className="gap-2">
            <Plus className="h-4 w-4" /> Nova Oportunidade
          </Button>
        </div>
      </div>

      {/* KPIs gerais */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Oportunidades em aberto</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold">{totaisGerais.total}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Valor total no pipeline</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold text-primary">{fmt(totaisGerais.valor)}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Ticket médio</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {fmt(totaisGerais.total === 0 ? 0 : totaisGerais.valor / totaisGerais.total)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Kanban */}
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Carregando funil…</div>
      ) : !visao || visao.colunas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground space-y-3">
            <p>Nenhum estágio cadastrado ainda.</p>
            <Button onClick={() => setNovoEstagioModal(true)} variant="outline" className="gap-2">
              <Plus className="h-4 w-4" /> Criar primeiro estágio
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2">
          {visao.colunas.map((col) => {
            const e = col.estagio;
            const corBorda = e.cor ?? '#3B82F6';
            const isOver = dragOver === e.id;
            return (
              <div
                key={e.id}
                onDragOver={(ev) => handleDragOver(ev, e.id)}
                onDragLeave={() => setDragOver(null)}
                onDrop={() => handleDrop(e.id)}
                className={`flex-shrink-0 w-80 rounded-lg border-2 transition-colors ${
                  isOver ? 'border-primary bg-primary/5' : 'border-border bg-muted/30'
                }`}
                style={{ borderTopColor: corBorda, borderTopWidth: 4 }}
              >
                <div className="p-3 flex items-center justify-between border-b bg-background/50">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: corBorda }} />
                    <h3 className="font-semibold flex items-center gap-1">
                      {e.nome}
                      {e.isGanho && <Trophy className="h-3 w-3 text-emerald-500" />}
                      {e.isPerda && <XCircle className="h-3 w-3 text-rose-500" />}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant="secondary" className="font-mono">{col.quantidadeOportunidades}</Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7"><MoreVertical className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setNovaOpModal({ open: true, estagioId: e.id })}>
                          <Plus className="h-4 w-4 mr-2" /> Nova oportunidade
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setEditandoEstagio(e)}>
                          <Pencil className="h-4 w-4 mr-2" /> Editar estágio
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setRemoveAlert({ tipo: 'estagio', id: e.id })}
                          className="text-destructive">
                          <Trash2 className="h-4 w-4 mr-2" /> Remover estágio
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                <div className="px-3 py-2 text-xs text-muted-foreground border-b bg-background/30">
                  {fmt(col.valorTotal)} · {e.probabilidadeDefault}% prob.
                </div>

                <div className="p-2 space-y-2 min-h-[200px] max-h-[calc(100vh-380px)] overflow-y-auto">
                  {col.oportunidades.map((o) => (
                    <Card
                      key={o.id}
                      draggable
                      onDragStart={() => handleDragStart(o.id)}
                      onDragEnd={handleDragEnd}
                      className={`cursor-grab active:cursor-grabbing transition-opacity ${
                        draggingId === o.id ? 'opacity-50' : ''
                      }`}>
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm truncate" title={o.titulo}>{o.titulo}</h4>
                            <p className="text-xs text-muted-foreground">Cliente #{o.clienteId}</p>
                          </div>
                          <GripVertical className="h-4 w-4 text-muted-foreground/50 shrink-0" />
                        </div>

                        <div className="flex items-center justify-between text-xs">
                          <span className="font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                            <DollarSign className="h-3 w-3" />
                            {fmt(o.valorEstimado)}
                          </span>
                          <Badge variant="outline" className="font-mono text-xs">{o.probabilidade}%</Badge>
                        </div>

                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          {o.dtPrevisaoFechamento ? (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(o.dtPrevisaoFechamento).toLocaleDateString('pt-BR')}
                            </span>
                          ) : <span />}
                          {o.vendedorId && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" /> #{o.vendedorId}
                            </span>
                          )}
                        </div>

                        <div className="flex gap-1 pt-1">
                          <Button size="sm" variant="outline" className="flex-1 h-7 text-xs"
                            onClick={() => setFecharModal({ open: true, oportunidade: o, ganho: true })}>
                            <Trophy className="h-3 w-3 mr-1" /> Ganhar
                          </Button>
                          <Button size="sm" variant="outline" className="flex-1 h-7 text-xs"
                            onClick={() => setFecharModal({ open: true, oportunidade: o, ganho: false })}>
                            <XCircle className="h-3 w-3 mr-1" /> Perder
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                            onClick={() => setRemoveAlert({ tipo: 'oportunidade', id: o.id })}>
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {col.oportunidades.length === 0 && (
                    <div className="text-center text-xs text-muted-foreground py-8 italic">
                      Solte uma oportunidade aqui
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <NovaOportunidadeModal
        open={novaOpModal.open}
        estagioInicial={novaOpModal.estagioId}
        estagios={estagios}
        onClose={() => setNovaOpModal({ open: false })}
        onSubmit={(req) => { m.criarOportunidade.mutate(req); setNovaOpModal({ open: false }); }}
      />

      <EstagioModal
        open={novoEstagioModal || !!editandoEstagio}
        estagio={editandoEstagio}
        onClose={() => { setNovoEstagioModal(false); setEditandoEstagio(null); }}
        onCreate={(req) => { m.criarEstagio.mutate(req); setNovoEstagioModal(false); }}
        onUpdate={(id, req) => { m.atualizarEstagio.mutate({ id, data: req }); setEditandoEstagio(null); }}
      />

      <FecharOportunidadeModal
        open={fecharModal.open}
        oportunidade={fecharModal.oportunidade}
        ganho={fecharModal.ganho ?? false}
        onClose={() => setFecharModal({ open: false })}
        onConfirm={(motivoPerda, observacao) => {
          if (fecharModal.oportunidade) {
            m.fecharOportunidade.mutate({
              id: fecharModal.oportunidade.id,
              data: { ganho: fecharModal.ganho ?? false, motivoPerda, observacao },
            });
          }
          setFecharModal({ open: false });
        }}
      />

      <DeleteConfirmDialog
        open={!!removeAlert}
        onOpenChange={(o) => !o && setRemoveAlert(null)}
        onConfirm={() => {
          if (!removeAlert) return;
          if (removeAlert.tipo === 'estagio') m.removerEstagio.mutate(removeAlert.id);
          else m.removerOportunidade.mutate(removeAlert.id);
          setRemoveAlert(null);
        }}
        title={removeAlert?.tipo === 'estagio' ? 'Remover estágio' : 'Remover oportunidade'}
        description={
          removeAlert?.tipo === 'estagio'
            ? 'O estágio só pode ser removido se não houver oportunidades vinculadas.'
            : 'Esta ação removerá a oportunidade e todo o seu histórico.'
        }
      />
    </div>
  );
};

// Origens de lead (espelha o ComboBox do desktop FrmCadOportunidade)
const ORIGENS_OPORTUNIDADE = [
  'Indicação', 'Site', 'WhatsApp', 'Marketplace', 'Ligação', 'Visita', 'Campanha', 'Outro',
] as const;

// ── Modal: Nova Oportunidade ────────────────────────────────────────────────

interface NovaOportunidadeProps {
  open: boolean;
  estagioInicial?: number;
  estagios: EstagioPipeline[];
  onClose: () => void;
  onSubmit: (req: CriarOportunidadeRequest) => void;
}

const NovaOportunidadeModal = ({ open, estagioInicial, estagios, onClose, onSubmit }: NovaOportunidadeProps) => {
  const [form, setForm] = useState<CriarOportunidadeRequest>({
    clienteId: 0, titulo: '', estagioId: estagioInicial ?? 0, valorEstimado: 0,
  });

  const reset = () => setForm({ clienteId: 0, titulo: '', estagioId: estagioInicial ?? 0, valorEstimado: 0 });

  const submit = () => {
    if (!form.titulo || !form.clienteId || !form.estagioId) return;
    onSubmit(form);
    reset();
  };

  const estagiosAtivos = estagios.filter((e) => e.isAtivo && !e.isGanho && !e.isPerda);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Nova Oportunidade</DialogTitle></DialogHeader>
        <div className="grid gap-3 py-2">
          <div>
            <Label>Título *</Label>
            <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Cliente ID *</Label>
              <Input type="number" value={form.clienteId || ''}
                onChange={(e) => setForm({ ...form, clienteId: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Vendedor ID</Label>
              <Input type="number" value={form.vendedorId || ''}
                onChange={(e) => setForm({ ...form, vendedorId: e.target.value ? Number(e.target.value) : undefined })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Estágio *</Label>
              <select value={form.estagioId} onChange={(e) => setForm({ ...form, estagioId: Number(e.target.value) })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value={0}>Selecione…</option>
                {estagiosAtivos.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
              </select>
            </div>
            <div>
              <Label>Valor estimado</Label>
              <Input type="number" step="0.01" value={form.valorEstimado || 0}
                onChange={(e) => setForm({ ...form, valorEstimado: Number(e.target.value) })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Previsão de fechamento</Label>
              <Input type="date" value={form.dtPrevisaoFechamento ?? ''}
                onChange={(e) => setForm({ ...form, dtPrevisaoFechamento: e.target.value || undefined })} />
            </div>
            <div>
              <Label>Origem</Label>
              <select value={form.origem ?? ''} onChange={(e) => setForm({ ...form, origem: e.target.value || undefined })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">Selecione…</option>
                {ORIGENS_OPORTUNIDADE.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label>Contato</Label>
            <Input placeholder="Nome da pessoa de contato no cliente" value={form.contato ?? ''}
              onChange={(e) => setForm({ ...form, contato: e.target.value || undefined })} />
          </div>
          <div>
            <Label>Descrição</Label>
            <Textarea rows={3} value={form.descricao ?? ''}
              onChange={(e) => setForm({ ...form, descricao: e.target.value || undefined })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }}>Cancelar</Button>
          <Button onClick={submit}>Criar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Modal: Estágio (criar/editar) ──────────────────────────────────────────

interface EstagioModalProps {
  open: boolean;
  estagio: EstagioPipeline | null;
  onClose: () => void;
  onCreate: (req: CriarEstagioRequest) => void;
  onUpdate: (id: number, req: Partial<CriarEstagioRequest>) => void;
}

const EstagioModal = ({ open, estagio, onClose, onCreate, onUpdate }: EstagioModalProps) => {
  const [form, setForm] = useState<CriarEstagioRequest>({ nome: '', ordem: 0 });

  const isEdit = !!estagio;
  useEffect(() => {
    if (estagio) {
      setForm({
        nome: estagio.nome, ordem: estagio.ordem, cor: estagio.cor,
        probabilidadeDefault: estagio.probabilidadeDefault,
        isGanho: estagio.isGanho, isPerda: estagio.isPerda,
      });
    } else {
      setForm({ nome: '', ordem: 0, cor: '#3B82F6', probabilidadeDefault: 0 });
    }
  }, [estagio]);

  const submit = () => {
    if (!form.nome) return;
    if (isEdit && estagio) onUpdate(estagio.id, form);
    else onCreate(form);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{isEdit ? 'Editar Estágio' : 'Novo Estágio'}</DialogTitle></DialogHeader>
        <div className="grid gap-3 py-2">
          <div>
            <Label>Nome *</Label>
            <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Ordem</Label>
              <Input type="number" value={form.ordem}
                onChange={(e) => setForm({ ...form, ordem: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Cor</Label>
              <Input type="color" value={form.cor ?? '#3B82F6'}
                onChange={(e) => setForm({ ...form, cor: e.target.value })} />
            </div>
            <div>
              <Label>Prob. (%)</Label>
              <Input type="number" min={0} max={100} value={form.probabilidadeDefault ?? 0}
                onChange={(e) => setForm({ ...form, probabilidadeDefault: Number(e.target.value) })} />
            </div>
          </div>
          <div className="flex gap-4 pt-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isGanho ?? false}
                onChange={(e) => setForm({ ...form, isGanho: e.target.checked, isPerda: e.target.checked ? false : form.isPerda })} />
              Estágio de ganho
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isPerda ?? false}
                onChange={(e) => setForm({ ...form, isPerda: e.target.checked, isGanho: e.target.checked ? false : form.isGanho })} />
              Estágio de perda
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit}>{isEdit ? 'Salvar' : 'Criar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Modal: Fechar (ganhar/perder) ──────────────────────────────────────────

interface FecharModalProps {
  open: boolean;
  oportunidade?: OportunidadeResumo;
  ganho: boolean;
  onClose: () => void;
  onConfirm: (motivoPerda?: string, observacao?: string) => void;
}

const FecharOportunidadeModal = ({ open, oportunidade, ganho, onClose, onConfirm }: FecharModalProps) => {
  const [motivo, setMotivo] = useState('');
  const [obs, setObs]       = useState('');

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {ganho ? <Trophy className="h-5 w-5 text-emerald-500" /> : <XCircle className="h-5 w-5 text-rose-500" />}
            {ganho ? 'Marcar como Ganha' : 'Marcar como Perdida'}
          </DialogTitle>
        </DialogHeader>
        <div className="py-2 space-y-3">
          {oportunidade && (
            <div className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{oportunidade.titulo}</span>
              {' · '}{fmt(oportunidade.valorEstimado)}
            </div>
          )}
          {!ganho && (
            <div>
              <Label>Motivo da perda</Label>
              <Input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                placeholder="Preço, prazo, concorrência…" />
            </div>
          )}
          <div>
            <Label>Observação</Label>
            <Textarea rows={3} value={obs} onChange={(e) => setObs(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setMotivo(''); setObs(''); onClose(); }}>Cancelar</Button>
          <Button onClick={() => { onConfirm(motivo || undefined, obs || undefined); setMotivo(''); setObs(''); }}>
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CrmPipeline;
